import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft,
  Sparkles,
  Loader2,
  Beaker,
  Brain,
  Target,
  Zap,
  CheckCircle,
  AlertCircle,
  FileText,
  Settings,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;

// Constants
const INDUSTRIES = [
  'Financial Services', 'Healthcare', 'Technology', 'Manufacturing',
  'Retail', 'Government', 'Education', 'Legal', 'Energy',
  'Telecommunications', 'General Business'
];

const DIFFICULTY_LEVELS = [
  { value: 'beginner', label: 'Beginner', description: 'Basic concepts and straightforward scenarios' },
  { value: 'intermediate', label: 'Intermediate', description: 'Complex scenarios with nuanced decisions' },
  { value: 'advanced', label: 'Advanced', description: 'Expert-level edge cases and compliance challenges' }
];

const SUGGESTED_TOPICS = [
  { topic: 'Sanctions Screening', industry: 'Financial Services', description: 'How to properly investigate screening alerts' },
  { topic: 'Data Privacy & POPIA', industry: 'Financial Services', description: 'Handling personal information under POPIA' },
  { topic: 'Conflict of Interest', industry: 'General Business', description: 'Identifying and managing conflicts' },
  { topic: 'Anti-Money Laundering', industry: 'Financial Services', description: 'Detecting and reporting suspicious activity' },
  { topic: 'Workplace Harassment Prevention', industry: 'General Business', description: 'Recognizing and responding to harassment' },
  { topic: 'Insider Trading', industry: 'Financial Services', description: 'Understanding material non-public information' },
  { topic: 'Patient Data Confidentiality', industry: 'Healthcare', description: 'Protecting patient health information' },
  { topic: 'Cybersecurity Awareness', industry: 'Technology', description: 'Recognizing and avoiding cyber threats' }
];

export default function AILabGenerator() {
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);
  const [generatedLab, setGeneratedLab] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [formData, setFormData] = useState({
    topic: '',
    industry: 'Financial Services',
    num_scenarios: 3,
    difficulty: 'intermediate',
    additional_context: '',
    include_mental_model: true
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const selectSuggestedTopic = (suggestion) => {
    setFormData(prev => ({
      ...prev,
      topic: suggestion.topic,
      industry: suggestion.industry
    }));
  };

  const generateLab = async () => {
    if (!formData.topic.trim()) {
      toast.error('Please enter a compliance topic');
      return;
    }

    setGenerating(true);
    setGeneratedLab(null);

    try {
      const response = await axios.post(`${API}/learning-labs-v2/ai/generate`, formData, { headers });
      
      if (response.data.success) {
        setGeneratedLab(response.data);
        toast.success(`Generated lab with ${response.data.lab.total_scenarios} scenarios!`);
      } else {
        toast.error('Generation failed');
      }
    } catch (err) {
      console.error('Generation error:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to generate learning lab';
      toast.error(errorMsg);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800">
        {/* Header */}
        <div className="border-b border-slate-700 bg-slate-900/80 backdrop-blur">
          <div className="max-w-5xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => navigate('/admin')} className="text-slate-400">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <div>
                  <h1 className="text-xl font-bold text-white flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-amber-400" />
                    AI Lab Generator
                  </h1>
                  <p className="text-sm text-slate-400">Generate complete learning labs from a topic prompt</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Main Form - Left Side */}
            <div className="lg:col-span-3 space-y-6">
              {/* Topic Input */}
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-amber-400" />
                  What compliance topic do you want to teach?
                </h2>
                
                <Textarea
                  value={formData.topic}
                  onChange={(e) => updateForm('topic', e.target.value)}
                  placeholder="e.g., Sanctions Screening: how to properly investigate and respond to screening alerts without automatically treating every match as a positive..."
                  className="bg-slate-700 border-slate-600 min-h-32 text-lg"
                  disabled={generating}
                />

                <p className="text-sm text-slate-400 mt-2">
                  Be as specific as possible. Include key concepts, scenarios you want covered, or specific regulations.
                </p>
              </div>

              {/* Quick Suggestions */}
              <div className="bg-slate-800/30 rounded-xl p-4 border border-slate-700/50">
                <h3 className="text-sm font-medium text-slate-400 mb-3">Quick Start Templates</h3>
                <div className="flex flex-wrap gap-2">
                  {SUGGESTED_TOPICS.map((suggestion, i) => (
                    <button
                      key={i}
                      onClick={() => selectSuggestedTopic(suggestion)}
                      disabled={generating}
                      className="px-3 py-1.5 rounded-full bg-slate-700/50 text-slate-300 text-sm hover:bg-amber-500/20 hover:text-amber-400 transition-colors border border-slate-600 hover:border-amber-500/30"
                      title={suggestion.description}
                    >
                      {suggestion.topic}
                    </button>
                  ))}
                </div>
              </div>

              {/* Configuration */}
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Settings className="w-5 h-5 text-amber-400" />
                  Configuration
                </h2>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-400 block mb-1">Industry</label>
                    <Select 
                      value={formData.industry} 
                      onValueChange={(v) => updateForm('industry', v)}
                      disabled={generating}
                    >
                      <SelectTrigger className="bg-slate-700 border-slate-600">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {INDUSTRIES.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm text-slate-400 block mb-1">Difficulty</label>
                    <Select 
                      value={formData.difficulty} 
                      onValueChange={(v) => updateForm('difficulty', v)}
                      disabled={generating}
                    >
                      <SelectTrigger className="bg-slate-700 border-slate-600">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DIFFICULTY_LEVELS.map(l => (
                          <SelectItem key={l.value} value={l.value}>
                            {l.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm text-slate-400 block mb-1">Number of Scenarios</label>
                    <Select 
                      value={String(formData.num_scenarios)} 
                      onValueChange={(v) => updateForm('num_scenarios', parseInt(v))}
                      disabled={generating}
                    >
                      <SelectTrigger className="bg-slate-700 border-slate-600">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="2">2 Scenarios</SelectItem>
                        <SelectItem value="3">3 Scenarios</SelectItem>
                        <SelectItem value="4">4 Scenarios</SelectItem>
                        <SelectItem value="5">5 Scenarios</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.include_mental_model}
                        onChange={(e) => updateForm('include_mental_model', e.target.checked)}
                        disabled={generating}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-amber-500 focus:ring-amber-500"
                      />
                      <span className="text-sm text-slate-300">Include Mental Model Framework</span>
                    </label>
                  </div>
                </div>

                {/* Advanced Options Toggle */}
                <button
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="mt-4 text-sm text-slate-400 hover:text-white flex items-center gap-1"
                >
                  <ChevronRight className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-90' : ''}`} />
                  Advanced Options
                </button>

                {showAdvanced && (
                  <div className="mt-4">
                    <label className="text-sm text-slate-400 block mb-1">Additional Context (Optional)</label>
                    <Textarea
                      value={formData.additional_context}
                      onChange={(e) => updateForm('additional_context', e.target.value)}
                      placeholder="Add specific requirements, regulations to reference, or examples you want included..."
                      className="bg-slate-700 border-slate-600"
                      disabled={generating}
                    />
                  </div>
                )}
              </div>

              {/* Generate Button */}
              <Button
                onClick={generateLab}
                disabled={generating || !formData.topic.trim()}
                className="w-full py-6 text-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                data-testid="generate-lab-btn"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating Lab... (this may take 30-60 seconds)
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Generate Learning Lab
                  </>
                )}
              </Button>
            </div>

            {/* Right Side - Info & Results */}
            <div className="lg:col-span-2 space-y-6">
              {/* What AI Creates */}
              <div className="bg-slate-800/30 rounded-xl p-5 border border-slate-700/50">
                <h3 className="font-medium text-white mb-3 flex items-center gap-2">
                  <Brain className="w-4 h-4 text-amber-400" />
                  What AI Creates
                </h3>
                <ul className="space-y-2 text-sm text-slate-400">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                    <span>Realistic scenarios with named characters and case details</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                    <span>Multiple-choice questions with educational feedback</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                    <span>Alert boxes highlighting critical decisions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                    <span>Comparison tables for data analysis scenarios</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                    <span>Key learning points and mental model frameworks</span>
                  </li>
                </ul>
              </div>

              {/* Tips */}
              <div className="bg-blue-500/10 rounded-xl p-5 border border-blue-500/20">
                <h3 className="font-medium text-blue-400 mb-3 flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Tips for Best Results
                </h3>
                <ul className="space-y-2 text-sm text-blue-200/80">
                  <li>• Be specific about the compliance topic or regulation</li>
                  <li>• Include the type of decisions learners should practice</li>
                  <li>• Mention specific roles (analyst, manager, officer)</li>
                  <li>• Reference relevant laws or frameworks (FICA, POPIA, etc.)</li>
                </ul>
              </div>

              {/* Generation Result */}
              {generatedLab && (
                <div className="bg-green-500/10 rounded-xl p-5 border border-green-500/30">
                  <h3 className="font-medium text-green-400 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Lab Generated Successfully!
                  </h3>
                  
                  <div className="space-y-3">
                    <div>
                      <span className="text-slate-400 text-sm">Title:</span>
                      <p className="text-white font-medium">{generatedLab.lab.title}</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="bg-slate-800/50 rounded p-2">
                        <span className="text-slate-400">Scenarios</span>
                        <p className="text-white font-bold">{generatedLab.lab.total_scenarios}</p>
                      </div>
                      <div className="bg-slate-800/50 rounded p-2">
                        <span className="text-slate-400">Questions</span>
                        <p className="text-white font-bold">{generatedLab.lab.total_questions}</p>
                      </div>
                    </div>

                    <div className="flex gap-2 mt-4">
                      <Button
                        onClick={() => navigate(`/admin/labs-v2/${generatedLab.lab_id}/edit`)}
                        className="flex-1 bg-amber-500 hover:bg-amber-600"
                        data-testid="edit-generated-lab-btn"
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Edit Lab
                      </Button>
                      <Button
                        onClick={() => navigate(`/labs-v2/${generatedLab.lab_id}`)}
                        variant="outline"
                        className="flex-1 border-green-500/50 text-green-400"
                        data-testid="preview-generated-lab-btn"
                      >
                        <Beaker className="w-4 h-4 mr-2" />
                        Preview
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Generating State */}
              {generating && (
                <div className="bg-amber-500/10 rounded-xl p-5 border border-amber-500/30">
                  <div className="flex items-center gap-3 mb-3">
                    <Loader2 className="w-5 h-5 text-amber-400 animate-spin" />
                    <span className="text-amber-400 font-medium">Generating your lab...</span>
                  </div>
                  <div className="space-y-2 text-sm text-amber-200/70">
                    <p>• Creating realistic scenarios and characters</p>
                    <p>• Writing questions with educational feedback</p>
                    <p>• Building content blocks and alerts</p>
                    {formData.include_mental_model && (
                      <p>• Designing mental model framework</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
